import type { Invoice as RxInvoice } from "$global-providers/rxdb-provider/collections/invoice";
import {
  type FinaliseInvoiceInput,
  Invoice,
} from "$routes/_lib/billing-v2/invoice";
import type {
  CalculatedInvoice,
  InvoicePayerReference,
} from "$routes/_lib/billing-v2/invoice-source";
import { PayerType } from "elephant-web/gql";
import type { RxDocument } from "rxdb";
import { invoiceRecordFromDocument } from "../invoice-record-from-document";
import { updateInvoiceDocument } from "../update-invoice-document";

type InsurerCharge = FinaliseInvoiceInput["insurerCharges"][number];
type InsurerPayer = Extract<InvoicePayerReference, { type: PayerType.Insurer }>;

const MAX_PAYER_REFRESHES = 3;

const payerKey = (payer: InsurerPayer): string =>
  payer.id ? `id:${payer.id}` : `name:${payer.name ?? ""}`;

const positiveInsurers = (invoice: CalculatedInvoice): InsurerPayer[] =>
  invoice.payers
    .filter(
      (payer) => payer.type === PayerType.Insurer && payer.balance.total > 0,
    )
    .map((payer) => ({
      type: PayerType.Insurer,
      name: payer.name,
      ...(payer.id ? { id: payer.id } : {}),
    }));

class MissingInsurerChargeIds extends Error {
  constructor(readonly payers: InsurerPayer[]) {
    super("Latest invoice contains insurers without allocated charge IDs");
  }
}

const insurerCharges = (
  invoice: CalculatedInvoice,
  idsByPayer: ReadonlyMap<string, string>,
): InsurerCharge[] => {
  const payers = positiveInsurers(invoice);
  const missing = payers.filter((payer) => !idsByPayer.has(payerKey(payer)));
  if (missing.length > 0) throw new MissingInsurerChargeIds(missing);
  return payers.map((payer) => ({
    payer,
    transactionId: idsByPayer.get(payerKey(payer)) as string,
  }));
};

/**
 * Financial IDs are allocated outside RxDB's retryable modifier. If a newer
 * revision introduces another positive insurer, the uncommitted modifier
 * aborts, allocates that payer's stable ID here, then retries the whole write.
 */
export const updateWithInsurerChargeIds = async (
  doc: RxDocument<RxInvoice>,
  {
    timestamp,
    actionedByUserId,
    createTransactionId,
    update,
  }: {
    timestamp: string;
    actionedByUserId: string;
    createTransactionId: () => string;
    update: (invoice: Invoice, charges: InsurerCharge[]) => CalculatedInvoice;
  },
): Promise<CalculatedInvoice> => {
  const idsByPayer = new Map<string, string>();
  const allocate = (payers: InsurerPayer[]) => {
    for (const payer of payers) {
      const key = payerKey(payer);
      if (!idsByPayer.has(key)) idsByPayer.set(key, createTransactionId());
    }
  };

  allocate(
    positiveInsurers(
      Invoice.fromRecord(invoiceRecordFromDocument(doc.getLatest())).toRecord(),
    ),
  );

  for (let refresh = 0; refresh <= MAX_PAYER_REFRESHES; refresh += 1) {
    try {
      return await updateInvoiceDocument(doc, {
        timestamp,
        actionedByUserId,
        update: (invoice) =>
          update(invoice, insurerCharges(invoice.toRecord(), idsByPayer)),
      });
    } catch (error) {
      if (!(error instanceof MissingInsurerChargeIds)) throw error;
      if (refresh === MAX_PAYER_REFRESHES) {
        throw new Error("Invoice insurer list kept changing during update");
      }
      allocate(error.payers);
    }
  }

  throw new Error("Invoice update could not allocate insurer charge IDs");
};
